/*
 * Copyright (c) Nexign, JSC, 2019-2020.
 */
/**
 * Component to filter and serch data
 *
 * @author Evgenij Tyan
 * @author Yakov Gushchin
 * @author Anton Panasenko
 * @author Aleksandr Perkhunov
 * @version 1.4.0
 * @since 1.0.0
 */

import { ChangeEvent, useEffect, useRef, useState } from 'react';

import classNames from 'classnames';
import get from 'lodash/get';
import invoke from 'lodash/invoke';
import map from 'lodash/map';
import Highlighter from 'react-highlight-words';

import { icons } from '../../../../assets';
import initTranslationCommon from '../../../../modules/internationalization/constants/common';
import ICommonTranslation from '../../../../modules/internationalization/types/common';
import { DropDownFilter, EllipseText, Input } from '../../../common';
import { DropDownRenderTypeEnum } from '../../../common/DropDownFilter/DropDownFilter';
import { EInputCursor, EInputType, IInputProps } from '../../../common/Input/Input';
import {
    FilterSearchInputDropdownContainerStyled,
    FilterSearchInputDropdownItemStyled,
    FilterSearchInputDropdownItemTextStyled,
} from './styled/FilterContainerStyled';

/**
 * @description Input filter props
 * @interface IFilterSearchInputProps
 * @property {IInputProps} input - toggle input props
 * @property {() => void} onChange - change method
 */
export interface IFilterSearchInputProps {
    input?: IInputProps;
    onChange?: (value: string) => void;
    onSearch?: (value: string) => void;
    searchResult?: string[];
    searchValue?: string;
    translation?: ICommonTranslation;
    tableName: string;
    value?: string;
}

/**
 * @description Filter with input.
 * @extends IFilterSearchInputProps
 * @see Input component
 */
const FilterSearchInput = (props: IFilterSearchInputProps) => {

    const [currentValue, setCurrentValue] = useState(props.value);
    const inputProps = { ...props.input, value: currentValue || '' };
    const placeholder = props.translation?.searchInput.placeholder ?? initTranslationCommon.searchInput.placeholder;
    const dropDownRef = useRef(null);

    useEffect(() => {
        setCurrentValue(props.value);
    }, [props.value]);

    useEffect(() => {
        const dropdownTooltipShown = get(dropDownRef, 'current.state.isToolTipShown');
        if ((props.searchResult?.length ?? 0) && !dropdownTooltipShown) {
            invoke(dropDownRef, 'current.toggleToolTip');
        }
    }, [props.searchResult]);

    const filterSearchInputDropdownClass = classNames(
        'filter__dropdown',
        props.searchResult?.length ? '' : 'no-items',
    );

    const onInputSearch = (event: ChangeEvent<HTMLInputElement>) => {
        setCurrentValue(event.target.value);
        if (props.onSearch  && event.target.value !== props.value) {
            props.onSearch(event.target.value);
        }
    };

    const onCloseClick = () => {
        if (currentValue !== props.value) {
            invoke(props, 'onChange', props.searchValue || currentValue);
        }
    };
    const onEnterHandler = () => {
        if (currentValue !== props.value) {
            invoke(dropDownRef, 'current.closeTooltip');
            invoke(props, 'onChange', props.searchValue || currentValue);
        }
    };

    const onItemClick = (value: string) => {
        invoke(dropDownRef, 'current.closeTooltip');
        invoke(props, 'onChange', value);
    };

    const renderDropDownItems = () =>
        map(props.searchResult, (result: string) =>
            <FilterSearchInputDropdownItemStyled
                key={result}
                onClick={() => onItemClick(result)}
            >
                <EllipseText>
                    <FilterSearchInputDropdownItemTextStyled>
                        <Highlighter
                            autoEscape={true}
                            highlightClassName="dropdown-item_highlight"
                            searchWords={[props.searchValue || '']}
                            textToHighlight={result}/>
                    </FilterSearchInputDropdownItemTextStyled>
                </EllipseText>
            </FilterSearchInputDropdownItemStyled>,
        );
    return (
        <DropDownFilter
            closeOnFilterButtonClick={false}
            filterButtonLabel={currentValue}
            icon={icons.searchSmallIcon}
            ref={dropDownRef}
            render={() => <FilterSearchInputDropdownContainerStyled
                className={filterSearchInputDropdownClass}/>
            }
            renderButton={() => <Input
                {...({ placeholder, ...inputProps })}
                className="drop-down__input"
                righticon={icons.searchSmallIcon}
                righticonhandler={onEnterHandler}
                onChange={onInputSearch}
                onEnter={onEnterHandler}
            />}
            renderInBody={true}
            renderType={DropDownRenderTypeEnum.input}
            showCloseButton={true}
            onCloseClick={onCloseClick}
        >
            {
                renderDropDownItems()
            }
        </DropDownFilter>
    );
};

FilterSearchInput.defaultProps = {
    input: {
        cursor: EInputCursor.auto,
        type: EInputType.small,
    },
    onChange: () => null,
    translation: initTranslationCommon,
};

export default FilterSearchInput;
